<template>
  <main class="tool-page tool-page-wide">
    <div class="translate-grid">
      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">Translate</p>
            <h2>输入与翻译</h2>
          </div>
          <div class="toolbar-inline">
            <el-button plain @click="swapLanguage">切换方向</el-button>
            <el-button plain @click="clearAll">清空</el-button>
          </div>
        </div>

        <el-form label-position="top" @submit.native.prevent>
          <div class="lang-row">
            <el-form-item label="源语言" class="lang-item">
              <el-select v-model="sourceLang" class="full-width">
                <el-option label="中文" value="zh-CN"></el-option>
                <el-option label="英文" value="en"></el-option>
              </el-select>
            </el-form-item>

            <el-form-item label="目标语言" class="lang-item">
              <el-select v-model="targetLang" class="full-width">
                <el-option label="英文" value="en"></el-option>
                <el-option label="中文" value="zh-CN"></el-option>
              </el-select>
            </el-form-item>
          </div>

          <p class="result-label">输入术语、字段说明或变量名</p>
          <el-form-item class="input-area-item">
            <el-input
              v-model="sourceText"
              type="textarea"
              :rows="16"
              resize="vertical"
              placeholder="例如：用户创建时间 / orderStatus / 是否删除"
            />
          </el-form-item>

          <div class="actions">
            <el-button type="primary" :loading="loading" @click="translateNow">立即翻译</el-button>
            <span class="helper-text">输入后会自动翻译，适合字段和变量名场景</span>
          </div>
        </el-form>
      </el-card>

      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">Result</p>
            <h2>翻译结果</h2>
          </div>
          <div class="toolbar-inline">
            <el-button @click="copyText(translatedText, '已复制翻译结果')">复制结果</el-button>
          </div>
        </div>

        <div class="result-stack">
          <div class="tree-panel">
            <p class="result-label">在线翻译</p>
            <div class="translate-result-box" :class="{ empty: !translatedText }">
              <p v-if="translatedText">{{ translatedText }}</p>
            </div>
          </div>

          <div class="naming-panel">
            <p class="result-label">命名建议</p>
            <div class="naming-list">
              <button
                v-for="item in namingVariants"
                :key="item.label"
                type="button"
                class="naming-chip"
                @click="copyText(item.value, `已复制 ${item.label}`)"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value || "--" }}</strong>
              </button>
              <div v-if="!namingVariants.length" class="translate-result-box empty"></div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <p class="status" :class="translateStatus.type">{{ translateStatus.message }}</p>
  </main>
</template>

<script>
const langNameMap = {
  "zh-CN": "中文",
  en: "英文",
};

function splitWords(text) {
  return text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/[^\w\s]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
}

function toCamelCase(words) {
  return words
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join("");
}

function toPascalCase(words) {
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join("");
}

function buildNamingVariants(baseText) {
  const words = splitWords(baseText);
  if (!words.length) {
    return [];
  }
  return [
    { label: "camelCase", value: toCamelCase(words) },
    { label: "PascalCase", value: toPascalCase(words) },
    { label: "snake_case", value: words.join("_") },
    { label: "kebab-case", value: words.join("-") },
  ];
}

export default {
  name: "TranslatePage",
  data() {
    return {
      sourceLang: "zh-CN",
      targetLang: "en",
      sourceText: "",
      translatedText: "",
      loading: false,
      debounceTimer: null,
      namingVariants: [],
      translateStatus: {
        message: "等待输入",
        type: "",
      },
    };
  },
  watch: {
    sourceText() {
      this.scheduleTranslate();
    },
    sourceLang() {
      if (this.sourceLang === this.targetLang) {
        this.targetLang = this.sourceLang === "zh-CN" ? "en" : "zh-CN";
      }
      this.scheduleTranslate();
    },
    targetLang() {
      if (this.sourceLang === this.targetLang) {
        this.sourceLang = this.targetLang === "zh-CN" ? "en" : "zh-CN";
      }
      this.scheduleTranslate();
    },
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    setStatus(message, type) {
      this.translateStatus = {
        message,
        type: type || "",
      };
    },
    scheduleTranslate() {
      if (this.debounceTimer) {
        window.clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = window.setTimeout(() => {
        this.translateNow(true);
      }, 450);
    },
    async translateNow(isAuto = false) {
      const text = this.sourceText.trim();
      if (!text) {
        this.translatedText = "";
        this.namingVariants = [];
        this.setStatus("等待输入", "");
        return;
      }

      this.loading = true;
      try {
        const url = new URL("https://api.mymemory.translated.net/get");
        url.searchParams.set("q", text);
        url.searchParams.set("langpair", `${this.sourceLang}|${this.targetLang}`);
        url.searchParams.set("mt", "1");

        const response = await fetch(url.toString());
        const data = await response.json();
        const translated = (((data || {}).responseData || {}).translatedText || "").trim();

        if (!translated) {
          throw new Error("未获取到翻译结果");
        }

        this.translatedText = translated;
        const baseForNaming = this.targetLang === "en" ? translated : this.sourceLang === "en" ? text : "";
        this.namingVariants = buildNamingVariants(baseForNaming);
        this.setStatus(
          isAuto ? `已自动翻译为${langNameMap[this.targetLang]}` : `已翻译为${langNameMap[this.targetLang]}`,
          "success"
        );
      } catch (error) {
        this.translatedText = "";
        this.namingVariants = [];
        this.setStatus(`翻译失败：${error.message}`, "error");
      } finally {
        this.loading = false;
      }
    },
    swapLanguage() {
      const source = this.sourceLang;
      this.sourceLang = this.targetLang;
      this.targetLang = source;
    },
    clearAll() {
      this.sourceText = "";
      this.translatedText = "";
      this.namingVariants = [];
      this.setStatus("已清空输入与结果", "success");
    },
    async copyText(text, successMessage) {
      const value = (text || "").trim();
      if (!value) {
        this.$message.error("没有可复制的内容");
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        this.$message.success(successMessage);
      } catch (error) {
        this.$message.error("复制失败，请检查浏览器权限");
      }
    },
  },
};
</script>

<style scoped>
.tool-page-wide {
  max-width: 1200px;
}

.translate-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.translate-grid .panel {
  height: 100%;
}

.translate-grid .panel .el-card__body {
  display: flex;
  flex-direction: column;
}

.lang-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.lang-item {
  margin-bottom: 16px;
}

.helper-text {
  color: #7b6855;
  font-size: 0.9rem;
  line-height: 1.6;
}

.result-stack {
  display: grid;
  gap: 18px;
}

.translate-result-box {
  min-height: 120px;
  padding: 16px;
  border: 1px solid rgba(74, 56, 33, 0.12);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
}

.translate-result-box.empty {
  min-height: 120px;
}

.translate-result-box p {
  margin: 0;
  color: #2b2117;
  line-height: 1.8;
  white-space: pre-wrap;
}

.naming-list {
  display: grid;
  gap: 10px;
}

.naming-chip {
  width: 100%;
  border: 1px solid rgba(74, 56, 33, 0.12);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.76);
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.naming-chip:hover {
  transform: translateY(-1px);
  border-color: rgba(200, 92, 53, 0.36);
  box-shadow: 0 12px 28px rgba(93, 59, 22, 0.08);
}

.naming-chip span {
  display: block;
  margin-bottom: 6px;
  color: #7b6855;
  font-size: 0.9rem;
}

.naming-chip strong {
  color: #2b2117;
  font-size: 1rem;
  font-family: "SFMono-Regular", "Consolas", "Liberation Mono", monospace;
}

@media (max-width: 960px) {
  .translate-grid,
  .lang-row {
    grid-template-columns: 1fr;
  }
}
</style>
